import React, { useState, useEffect } from 'react';
import './WaitingListModal.scss';
import axios from '@/Data/Axios';
import { createI18n } from '@/Utils/i18n';
import type { University } from 'ai-api/types/admission';
import { useSession } from '@/Recoil/Session/Session';
import { useUsers } from '@/Recoil/User';

const translations = {
    'University Waiting List': { id: 'Daftar Tunggu Universitas', ko: '대학 대기 명단' },
    'Full Name': { id: 'Nama Lengkap', ko: '성명' },
    'Email Address': { id: 'Alamat Email', ko: '이메일 주소' },
    'Phone Number': { id: 'Nomor Telepon', ko: '전화번호' },
    'Preferred Program': { id: 'Program Pilihan', ko: '희망 프로그램' },
    'Message': { id: 'Pesan', ko: '메시지' },
    'Join Waiting List': { id: 'Gabung Daftar Tunggu', ko: '대기 명단 등록' },
    'Processing...': { id: 'Memproses...', ko: '처리 중...' },
    'Required field': { id: 'Bidang wajib', ko: '필수 입력' },
    'Registration successful': { id: 'Pendaftaran berhasil', ko: '등록 완료' },
    'Registration failed': { id: 'Pendaftaran gagal', ko: '등록 실패' }
};

const t = createI18n(translations);

interface FormData {
    name: string;
    email: string;
    phone: string;
    program: string;
    message: string;
}

interface WaitingListModalProps {
    isOpen: boolean;
    onClose: () => void;
    university: University | null;
}

const WaitingListModal: React.FC<WaitingListModalProps> = ({ isOpen, onClose, university }) => {
    const {session} = useSession();
    const {syncProfiles, updateProfile} = useUsers();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        name: session.name ?? '',
        email: session.email ?? '',
        phone: '',
        program: '',
        message: ''
    });

    useEffect(() => {
        if (session.addressid){
            syncProfiles([session.addressid]);
        }
        setFormData(prev => ({
            ...prev,
            name: session.name ?? '',
            email: session.email ?? ''
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.email || !formData.phone) {
            alert(t('Required field'));
            return;
        }
        
        updateProfile({
            name: formData.name,
            email: formData.email,
            phonenumber: formData.phone
        });

        setIsSubmitting(true);

        try {
            const currentDate = new Date().toLocaleString();
            const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>University Waiting List</title>
                    <style>
                        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; background-color: #f4f4f4; }
                        .container { max-width: 600px; margin: 0 auto; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
                        .header { background: linear-gradient(135deg, #7b4397 0%, #dc2430 100%); color: white; padding: 30px; text-align: center; }
                        .content { padding: 30px; }
                        .info-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                        .info-table th, .info-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
                        .info-table th { background-color: #f8f9fa; font-weight: bold; }
                        .footer { background-color: #f8f9fa; padding: 20px; text-align: center; color: #666; font-size: 14px; }
                    </style>
                </head>
                <body>
                    <div class="container">
                        <div class="header">
                            <h1>🎓 University Waiting List</h1>
                            <p>New registration for ${university?.name}</p>
                        </div>
                        <div class="content">
                            <h2>Registration Details</h2>
                            <table class="info-table">
                                <tr>
                                    <th>Field</th>
                                    <th>Response</th>
                                </tr>
                                <tr>
                                    <td><strong>University</strong></td>
                                    <td>${university?.name}</td>
                                </tr>
                                <tr>
                                    <td><strong>Name</strong></td>
                                    <td>${formData.name}</td>
                                </tr>
                                <tr>
                                    <td><strong>Email</strong></td>
                                    <td>${formData.email}</td>
                                </tr>
                                <tr>
                                    <td><strong>Phone</strong></td>
                                    <td>${formData.phone}</td>
                                </tr>
                                <tr>
                                    <td><strong>Preferred Program</strong></td>
                                    <td>${formData.program || 'Not specified'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Message</strong></td>
                                    <td>${formData.message || 'None'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Registration Time</strong></td>
                                    <td>${currentDate}</td>
                                </tr>
                            </table>
                        </div>
                        <div class="footer">
                            <p>This email was generated from the University Admission waiting list.</p>
                            <p>Korean Mate • <a href="https://koreanmate.app">koreanmate.app</a></p>
                        </div>
                    </div>
                </body>
                </html>
            `;

            const response = await axios.post('/admin/trigger-email', {
                email: 'dl_mate@webtoon.today',
                subject: `[🎓Waiting List] ${university?.name} - ${formData.name}`,
                body: htmlContent,
                groups: 'dl_mate@webtoon.today'
            });

            if (response.data && response.data.code === 200) {
                alert(t('Registration successful'));
                setFormData({ name: '', email: '', phone: '', program: '', message: '' });
                onClose();
            } else {
                throw new Error('Email sending failed');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            alert(t('Registration failed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle ESC key
    useEffect(() => {
        const handleEscKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEscKey);
        }

        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFormData({ name: '', email: '', phone: '', program: '', message: '' });
        }
    }, [isOpen]);

    if (!isOpen || !university) return null;

    return (
        <>
            <div className="modal-overlay" onClick={onClose} />
            <div className="waiting-list-modal">
                <button className="close-button" onClick={onClose}>×</button>
                <h2>{t('University Waiting List')}</h2>
                <div className="university-info">
                    {university.logo_url && (
                        <img src={university.logo_url} alt={university.name} className="university-logo" />
                    )}
                    <h3>{university.name}</h3>
                    <p>{university.location}</p>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
                        <div className="form-group">
                            <label htmlFor="name">{t('Full Name')} *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                placeholder={t('Full Name')}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">{t('Phone Number')} *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                placeholder="+62 812345678"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">{t('Email Address')} *</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="your.email@example.com"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="program">{t('Preferred Program')}</label>
                        <select
                            id="program"
                            name="program"
                            value={formData.program}
                            onChange={handleInputChange}
                        >
                            <option value="">Select a program</option>
                            <option value="undergraduate">Undergraduate</option>
                            <option value="masters">Master's Degree</option>
                            <option value="phd">PhD Program</option>
                            <option value="exchange">Exchange Program</option>
                            <option value="language">Language Course</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="message">{t('Message')}</label>
                        <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            rows={4}
                            placeholder="Any questions or comments?"
                        />
                    </div>

                    <button
                        type="submit"
                        className="submit-button"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? t('Processing...') : t('Join Waiting List')}
                    </button>
                </form>
            </div>
        </>
    );
};

export default WaitingListModal;